import { useReducer } from 'react'

const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_AUTOMATIONS: 'SET_AUTOMATIONS',
  SET_CAMPAIGNS: 'SET_CAMPAIGNS',
  SET_TEMPLATES: 'SET_TEMPLATES',
  SET_TAB_VALUE: 'SET_TAB_VALUE',
  SET_CAMPAIGN_SEARCH_TERM: 'SET_CAMPAIGN_SEARCH_TERM',
  SET_TEST_RESULTS: 'SET_TEST_RESULTS',
  SET_TESTED_AUTOMATION: 'SET_TESTED_AUTOMATION',
  SET_EMAIL_STATUS: 'SET_EMAIL_STATUS',
  UPDATE_EMAIL_CAMPAIGN_STATUS: 'UPDATE_EMAIL_CAMPAIGN_STATUS',
  RESET_EMAIL_STATUS: 'RESET_EMAIL_STATUS',
  TOGGLE_DIALOG: 'TOGGLE_DIALOG',
  SET_SELECTED_AUTOMATION: 'SET_SELECTED_AUTOMATION',
  SET_SELECTED_TEMPLATE: 'SET_SELECTED_TEMPLATE',
  SET_TEST_MODE: 'SET_TEST_MODE',
  SET_TEST_EMAIL: 'SET_TEST_EMAIL',
  UPDATE_NEW_AUTOMATION: 'UPDATE_NEW_AUTOMATION',
  UPDATE_NEW_TEMPLATE: 'UPDATE_NEW_TEMPLATE',
  RESET_NEW_AUTOMATION: 'RESET_NEW_AUTOMATION',
  RESET_NEW_TEMPLATE: 'RESET_NEW_TEMPLATE',
  UPDATE_AUTOMATION_ENABLED: 'UPDATE_AUTOMATION_ENABLED'
}

const initialState = {
  automations: [],
  campaigns: [],
  templates: [],
  testResults: [],
  
  emailStatus: {
    isRunning: false,
    results: [],
    totalCampaigns: 0,
    processedCampaigns: 0,
    testEmail: ''
  },
  
  loading: true,
  error: null,
  tabValue: 0,
  campaignSearchTerm: '',
  
  selectedAutomation: null,
  selectedTemplate: null,
  testedAutomationName: '',
  testedAutomationId: null,
  
  testMode: false,
  testModeEmail: '',
  testEmail: '',
  
  createDialogOpen: false,
  editDialogOpen: false,
  campaignsDialogOpen: false,
  testResultsDialogOpen: false,
  testPromptDialogOpen: false,
  emailStatusDialogOpen: false,
  templateDialogOpen: false,
  templateTestDialogOpen: false,
  templatePreviewDialogOpen: false,
  isCreatingTemplateFromAutomation: false,
  
  newAutomation: {
    name: '',
    type: 'plan_usage',
    enabled: true,
    template_id: null,
    config: {
      trigger: { threshold: 80, metric: 'plan_usage' }
    }
  },
  newTemplate: {
    name: '',
    subject: '',
    body: '',
    from_email: 'support@vetavirtual.com',
    include_dashboard_button: true
  }
}

function notificationReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload }
    
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload }
    
    case ACTIONS.SET_AUTOMATIONS:
      return { ...state, automations: action.payload }
    
    case ACTIONS.SET_CAMPAIGNS:
      return { ...state, campaigns: action.payload }
    
    case ACTIONS.SET_TEMPLATES:
      return { ...state, templates: action.payload }
    
    case ACTIONS.SET_TAB_VALUE:
      return { ...state, tabValue: action.payload }
    
    case ACTIONS.SET_CAMPAIGN_SEARCH_TERM:
      return { ...state, campaignSearchTerm: action.payload }
    
    case ACTIONS.SET_TEST_RESULTS:
      return { 
        ...state, 
        testResults: action.payload.results,
        testedAutomationName: action.payload.automationName || state.testedAutomationName,
        testedAutomationId: action.payload.automationId || state.testedAutomationId
      }
    
    case ACTIONS.SET_TESTED_AUTOMATION:
      return { 
        ...state, 
        testedAutomationName: action.payload.name,
        testedAutomationId: action.payload.id
      }
    
    case ACTIONS.TOGGLE_DIALOG:
      return { ...state, [action.payload.dialog]: action.payload.open }
    
    case ACTIONS.SET_SELECTED_AUTOMATION:
      return { ...state, selectedAutomation: action.payload }
    
    case ACTIONS.SET_SELECTED_TEMPLATE:
      return { ...state, selectedTemplate: action.payload }
    
    case ACTIONS.SET_TEST_MODE:
      return { 
        ...state, 
        testMode: action.payload.enabled,
        testModeEmail: action.payload.email || state.testModeEmail
      }
    
    case ACTIONS.SET_TEST_EMAIL:
      return { ...state, testEmail: action.payload }
    
    case ACTIONS.UPDATE_NEW_AUTOMATION:
      return { 
        ...state, 
        newAutomation: { ...state.newAutomation, ...action.payload }
      }
    
    case ACTIONS.UPDATE_NEW_TEMPLATE:
      return { 
        ...state, 
        newTemplate: { ...state.newTemplate, ...action.payload }
      }
    
    case ACTIONS.RESET_NEW_AUTOMATION:
      return { 
        ...state, 
        newAutomation: initialState.newAutomation
      }
    
    case ACTIONS.RESET_NEW_TEMPLATE:
      return { 
        ...state, 
        newTemplate: initialState.newTemplate
      }
    
    case ACTIONS.SET_EMAIL_STATUS:
      return {
        ...state,
        emailStatus: {
          ...state.emailStatus,
          ...action.payload
        }
      }
    
    case ACTIONS.UPDATE_EMAIL_CAMPAIGN_STATUS:
      return {
        ...state,
        emailStatus: {
          ...state.emailStatus,
          results: state.emailStatus.results.map(result =>
            result.campaignName === action.payload.campaignName
              ? { ...result, ...action.payload.status }
              : result
          ),
          processedCampaigns: state.emailStatus.results.filter(r => 
            r.status === 'sent' || r.status === 'error' || 
            (r.campaignName === action.payload.campaignName && (action.payload.status.status === 'sent' || action.payload.status.status === 'error'))
          ).length
        }
      }
    
    case ACTIONS.RESET_EMAIL_STATUS:
      return {
        ...state,
        emailStatus: {
          isRunning: false,
          results: [],
          totalCampaigns: 0,
          processedCampaigns: 0,
          testEmail: ''
        }
      }
    
    case ACTIONS.UPDATE_AUTOMATION_ENABLED:
      return {
        ...state,
        automations: state.automations.map(automation =>
          automation.id === action.payload.automationId
            ? { ...automation, enabled: action.payload.enabled }
            : automation
        )
      }
    
    default:
      return state
  }
}

export const useNotificationReducer = () => {
  const [state, dispatch] = useReducer(notificationReducer, initialState)
  
  const actions = {
    setLoading: (loading) => dispatch({ type: ACTIONS.SET_LOADING, payload: loading }),
    setError: (error) => dispatch({ type: ACTIONS.SET_ERROR, payload: error }),
    setAutomations: (automations) => dispatch({ type: ACTIONS.SET_AUTOMATIONS, payload: automations }),
    setCampaigns: (campaigns) => dispatch({ type: ACTIONS.SET_CAMPAIGNS, payload: campaigns }),
    setTemplates: (templates) => dispatch({ type: ACTIONS.SET_TEMPLATES, payload: templates }),
    setTabValue: (value) => dispatch({ type: ACTIONS.SET_TAB_VALUE, payload: value }),
    setCampaignSearchTerm: (term) => dispatch({ type: ACTIONS.SET_CAMPAIGN_SEARCH_TERM, payload: term }),
    setTestResults: (results, automationName, automationId) => 
      dispatch({ type: ACTIONS.SET_TEST_RESULTS, payload: { results, automationName, automationId } }),
    setTestedAutomation: (name, id) => 
      dispatch({ type: ACTIONS.SET_TESTED_AUTOMATION, payload: { name, id } }),
    toggleDialog: (dialog, open) => dispatch({ type: ACTIONS.TOGGLE_DIALOG, payload: { dialog, open } }),
    setSelectedAutomation: (automation) => dispatch({ type: ACTIONS.SET_SELECTED_AUTOMATION, payload: automation }),
    setSelectedTemplate: (template) => dispatch({ type: ACTIONS.SET_SELECTED_TEMPLATE, payload: template }),
    setTestMode: (enabled, email) => dispatch({ type: ACTIONS.SET_TEST_MODE, payload: { enabled, email } }),
    setTestEmail: (email) => dispatch({ type: ACTIONS.SET_TEST_EMAIL, payload: email }),
    updateNewAutomation: (updates) => dispatch({ type: ACTIONS.UPDATE_NEW_AUTOMATION, payload: updates }),
    updateNewTemplate: (updates) => dispatch({ type: ACTIONS.UPDATE_NEW_TEMPLATE, payload: updates }),
    resetNewAutomation: () => dispatch({ type: ACTIONS.RESET_NEW_AUTOMATION }),
    resetNewTemplate: () => dispatch({ type: ACTIONS.RESET_NEW_TEMPLATE }),
    
    setEmailStatus: (status) => dispatch({ type: ACTIONS.SET_EMAIL_STATUS, payload: status }),
    updateEmailCampaignStatus: (campaignName, status) => 
      dispatch({ type: ACTIONS.UPDATE_EMAIL_CAMPAIGN_STATUS, payload: { campaignName, status } }),
    resetEmailStatus: () => dispatch({ type: ACTIONS.RESET_EMAIL_STATUS }),
    
    updateAutomationEnabled: (automationId, enabled) => 
      dispatch({ type: ACTIONS.UPDATE_AUTOMATION_ENABLED, payload: { automationId, enabled } })
  }
  
  return [state, actions]
}

export { ACTIONS, notificationReducer, initialState }